/**
 * What the five setup steps are *called*, where each one is done, and how a
 * blocked step says what it is waiting on.
 *
 * The ids, the order, the prerequisites and the three states all live in
 * `@wend/shared` and are never re-declared here. This file holds only words and
 * paths, and it holds them because the contract deliberately does not: labels
 * churn, ids must not, and a shared package that carried copy would have the API
 * shipping a wording change.
 *
 * **The map is a total `Record<OnboardingStepId, …>` on purpose.** A sixth step
 * added to the contract fails to compile here rather than rendering a row with
 * no label and no way to act on it. That is the same trick `ONBOARDING_STEP_VALUES`
 * plays on the API's five reads, and it is the only thing keeping the two halves
 * of this slice honest with each other.
 *
 * ## The wording rules, each of which had a wrong answer available
 *
 * **Step 1 names Amazon SES rather than "a sending account".** The id is
 * channel-neutral because ids should not churn, but the read behind it only sees
 * an `integrations` row with `kind = 'ses'`. A workspace that connects Meta and
 * is then told it has not connected a sending account would go looking for a
 * bug, and the contract's header names this exact sentence as the copy
 * consequence of the email-first hole it leaves open.
 *
 * **Nothing here says "approved".** The obvious phrasing for what blocks a test
 * send is *needs an approved template*, and it is false: step 3's evidence is a
 * `templates` row with `type = 'email'` and no status filter, so a draft ticks
 * it. Writing "approved" would make the panel claim a fact nobody read. The
 * consequence is left standing rather than papered over, and it is worth knowing
 * about: a workspace whose only email template is a draft sees step 3 done and
 * step 5 ready, and the campaign wizard then refuses to schedule until the
 * template is approved — `campaignScheduleIssues` says so on the screen where it
 * happens. Fixing that properly means narrowing the contract's step-3 read, and
 * the contract is not this builder's to edit.
 *
 * **Step 1's destination is a tab, not a page.** Slice 16 turned `/integrations`
 * into a tab per channel and left the bare path resolving to Email, so this
 * step's link kept working by luck rather than by intent — and the luck runs out
 * the day a Flow source is added to the head of `INTEGRATION_TAB_VALUES`, which
 * is the whole purpose of that rail. The href is built by `integrationTabPath`
 * rather than written out, for the same reason nothing else in this file
 * restates a contract value. That helper is deliberately in a module with no
 * React in it: this one has a single type import and is loaded by two verify
 * scripts, and reaching a `<TabList>` render to learn one string is not a trade
 * worth making.
 *
 * **Steps 3 and 4 share `/templates` and do not share a label.** There is no
 * route that sends a test on its own: `TestSendPanel` lives inside the template
 * editor at `/templates/:templateId`, and a panel that has never seen a template
 * has no id to put in that path. So the honest destination for both is the list,
 * and the two action labels say which of the two errands you are on — "New
 * template" versus "Open a template" — rather than pretending to two routes.
 *
 * **No action anywhere adds credits, and that absence is deliberate rather than
 * an oversight.** A tenant cannot top up their own balance; grants are an admin
 * action against an append-only ledger. A credits step is not in the contract
 * and must not be invented here, because the one thing this panel promises is
 * that every step has something the reader can actually do — and a button that
 * opens a screen where the answer is "ask us" is the fastest way to teach
 * somebody that the checklist is decorative. The balance is already stated on
 * the Overview's credits card and on `/credits`, which is where a person who has
 * run out finds out why a send stopped.
 *
 * ## Why the blocked sentence is composed rather than written out per step
 *
 * {@link blockedSentence} builds its wording from the same `blockedNoun` map the
 * labels come from, so "needs an email template" and the row labelled "Build an
 * email template" cannot drift into naming the same thing two ways. The
 * alternative — one hand-written sentence per step — reads slightly better and
 * goes stale the moment `ONBOARDING_PREREQUISITES` changes, silently, because
 * nothing type-checks a sentence against a list.
 *
 * It takes a non-empty tuple, matching the contract's `blockedBy`, so there is
 * no "nothing blocks it" branch to get wrong; and it does not sort, because the
 * contract already returns the unmet prerequisites in step order and a second
 * opinion about that order is exactly what this file is trying not to have.
 */
import { type OnboardingStepId } from '@wend/shared';

import { integrationTabPath } from '../integrations/integration-tab-url';

export interface OnboardingStepDisplay {
  /** The row's own words. Imperative, and it names what is really read. */
  readonly label: string;
  /**
   * The one thing a `ready` step offers. There is never a second action: a row
   * with a choice on it is a row somebody stops to think about, and the whole
   * point of the panel is that there is one obvious next move.
   */
  readonly action: { readonly label: string; readonly href: string };
  /**
   * How this step is referred to when it is what is missing — "Needs **Amazon
   * SES** and **an email template**." A noun phrase, lower case unless it is a
   * proper name, and never a clause.
   */
  readonly blockedNoun: string;
}

export const ONBOARDING_STEP_DISPLAY: Readonly<Record<OnboardingStepId, OnboardingStepDisplay>> = {
  connect_sending_account: {
    label: 'Connect Amazon SES',
    action: { label: 'Connect', href: integrationTabPath('email') },
    blockedNoun: 'Amazon SES',
  },
  import_contacts: {
    label: 'Import your contacts',
    action: { label: 'Upload a list', href: '/audiences/upload' },
    blockedNoun: 'contacts',
  },
  build_template: {
    label: 'Build an email template',
    action: { label: 'New template', href: '/templates' },
    blockedNoun: 'an email template',
  },
  send_test: {
    label: 'Send yourself a test',
    action: { label: 'Open a template', href: '/templates' },
    blockedNoun: 'a test send',
  },
  run_campaign: {
    label: 'Run your first campaign',
    action: { label: 'New campaign', href: '/campaigns/new' },
    blockedNoun: 'a campaign',
  },
};

/**
 * `a`, `a and b`, `a, b and c`. No serial comma — the rest of the product's
 * prose does not use one.
 *
 * Written as a fold rather than `slice(-1)` because `noUncheckedIndexedAccess`
 * turns the tidy version into a `?? ''` fallback, and a fallback on a branch
 * that cannot happen is a place for a wrong answer to hide.
 */
function joinWithAnd(items: readonly [string, ...string[]]): string {
  return items.reduce((sentence, item, index) => {
    if (index === 0) return item;
    return index === items.length - 1 ? `${sentence} and ${item}` : `${sentence}, ${item}`;
  }, '');
}

/**
 * What a blocked step is waiting on, as one short sentence.
 *
 * The reason this is a sentence and not a greyed row: a step that is merely
 * dimmed is a step somebody clicks at, gets nothing from, and concludes the app
 * is broken. Naming the missing thing turns a dead end into a redirection, and
 * the row above it already carries the action that supplies it.
 */
export function blockedSentence(
  blockedBy: readonly [OnboardingStepId, ...OnboardingStepId[]],
): string {
  const [first, ...rest] = blockedBy;
  const nouns: [string, ...string[]] = [
    ONBOARDING_STEP_DISPLAY[first].blockedNoun,
    ...rest.map((id) => ONBOARDING_STEP_DISPLAY[id].blockedNoun),
  ];

  return `Needs ${joinWithAnd(nouns)}.`;
}
